/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Divider,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Breadcrumbs,
  Link,
  Chip,
  Snackbar,
  Alert,
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { getTenantById, getTenantFeatures, updateFeatureFlag } from '../../utils/api';

function FeatureManagement() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [features, setFeatures] = useState([]);
  const [openLogoDialog, setOpenLogoDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Regrouper les fonctionnalités par catégorie
  const featuresByCategory = features.reduce((acc, feature) => {
    const category = feature.category || 'Autres';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(feature);
    return acc;
  }, {});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [tenantData, featuresData] = await Promise.all([
          getTenantById(tenantId),
          getTenantFeatures(tenantId)
        ]);
        setTenant(tenantData);
        setFeatures(featuresData);
      }
      catch (err) {
        console.error('Erreur lors du chargement des données:', err);
        setError(err.message || 'Une erreur est survenue lors du chargement des données');
      }
      finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tenantId]);

  const handleToggleFeature = async (featureKey, currentState, isLocked) => {
    if (isLocked) {
      setSnackbar({
        open: true,
        message: 'Fonctionnalité verrouillée. Veuillez contacter Drivesoft (contact@drivesoft.fr) pour l\'activer.',
        severity: 'warning',
      });
      return;
    }
    try {
      await updateFeatureFlag(tenantId, featureKey, !currentState);

      // Mettre à jour l'état local
      setFeatures(features.map((feature) => (feature.feature_key === featureKey
        ? { ...feature, is_enabled: !currentState }
        : feature)));

      setSnackbar({
        open: true,
        message: `Fonctionnalité ${!currentState ? 'activée' : 'désactivée'} avec succès`,
        severity: 'success',
      });
    }
    catch (err) {
      console.error('Erreur lors de la mise à jour de la fonctionnalité:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Une erreur est survenue lors de la mise à jour de la fonctionnalité',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleBack = () => {
    navigate('/admin/tenants');
  };

  const handleOpenLogoDialog = () => {
    setOpenLogoDialog(true);
  };

  const handleCloseLogoDialog = () => {
    setOpenLogoDialog(false);
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh'
      }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3
      }}
      >
        <div>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            <Link color="inherit" href="#" onClick={() => navigate('/admin/dashboard')}>
              Dashboard
            </Link>
            <Link color="inherit" href="#" onClick={() => navigate('/admin/tenants')}>
              Tenants
            </Link>
            <Typography color="textPrimary">{tenant?.company_name || 'Fonctionnalités'}</Typography>
          </Breadcrumbs>
          <Typography variant="h4" sx={{ mt: 1 }}>
            Gestion des Fonctionnalités
          </Typography>
        </div>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Retour
        </Button>
      </Box>

      {tenant && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {tenant.logo_url ? (
              <Avatar
                src={tenant.logo_url}
                alt={tenant.company_name}
                sx={{
                  width: 64, height: 64, mr: 2, cursor: 'pointer'
                }}
                onClick={handleOpenLogoDialog}
              />
            ) : (
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  mr: 2,
                  bgcolor: tenant.primary_color || 'primary.main'
                }}
              >
                {tenant.company_name.charAt(0).toUpperCase()}
              </Avatar>
            )}
            <Typography variant="h5" color="primary" gutterBottom>
              {tenant.company_name}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Sous-domaine
              </Typography>
              <Typography variant="body1">
                {tenant.subdomain}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Statut
              </Typography>
              <Chip
                label={tenant.status}
                color={
                  tenant.status === 'active' ? 'success'
                    : tenant.status === 'trial' ? 'info'
                      : tenant.status === 'suspended' ? 'warning' : 'error'
                }
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Plan
              </Typography>
              <Typography variant="body1">
                {tenant.plan_name || 'Aucun plan'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Max Utilisateurs
              </Typography>
              <Typography variant="body1">
                {tenant.max_users || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Date de création
              </Typography>
              <Typography variant="body1">
                {new Date(tenant.created_at).toLocaleDateString()}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Période d'essai
              </Typography>
              <Typography variant="body1">
                {tenant.trial_ends_at
                  ? new Date(tenant.trial_ends_at).toLocaleDateString()
                  : 'N/A'}
              </Typography>
            </Grid>
            {tenant.contact_email && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Contact
                </Typography>
                <Typography variant="body1">
                  {tenant.contact_email}
                  {tenant.contact_name && ` (${tenant.contact_name})`}
                </Typography>
              </Grid>
            )}
            {tenant.signature_name && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Signature
                </Typography>
                <Typography variant="body1">
                  {tenant.signature_name}
                </Typography>
              </Grid>
            )}
          </Grid>

          {tenant.primary_color && tenant.secondary_color && (
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: tenant.primary_color,
                    borderRadius: '4px',
                  }}
                />
                <Typography variant="body2">Couleur primaire</Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: tenant.secondary_color,
                    borderRadius: '4px',
                  }}
                />
                <Typography variant="body2">Couleur secondaire</Typography>
              </Box>
            </Box>
          )}
        </Paper>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Fonctionnalités disponibles
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Activez ou désactivez les fonctionnalités pour ce tenant. Les modifications prennent effet immédiatement.
        </Typography>
      </Box>

      {Object.keys(featuresByCategory).length > 0 ? (
        Object.entries(featuresByCategory).map(([category, categoryFeatures]) => (
          <Box key={category} sx={{ mb: 4 }}>
            <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
              {category}
            </Typography>
            <Grid container spacing={3}>
              {categoryFeatures.map((feature) => (
                <Grid item xs={12} sm={6} md={4} key={feature.feature_key}>
                  <Card variant="outlined">
                    <CardHeader
                      title={feature.display_name}
                      action={(
                        <>
                          <FormControlLabel
                            control={(
                              <Switch
                                checked={feature.is_enabled}
                                onChange={() => handleToggleFeature(feature.feature_key, feature.is_enabled, feature.is_locked)}
                                color="primary"
                                disabled={feature.is_locked}
                              />
)}
                            label={feature.is_enabled ? 'Activé' : 'Désactivé'}
                          />
                          {feature.is_locked && (
                          <Chip
                            label="Verrouillé"
                            color="warning"
                            size="small"
                            sx={{ ml: 1 }}
                          />
                          )}
                        </>
)}
                    />
                    <Divider />
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description || 'Aucune description disponible'}
                      </Typography>
                      {feature.is_locked && (
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                          Cette fonctionnalité est désactivée au niveau global. Contactez {' '}
                          <Link href="mailto:contact@drivesoft.fr" underline="hover">
                            contact@drivesoft.fr
                          </Link> pour demander l'activation.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">
            Aucune fonctionnalité disponible pour ce tenant.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{ mt: 2 }}
          >
            Ajouter une fonctionnalité
          </Button>
        </Paper>
      )}

      {/* Logo Dialog */}
      <Dialog open={openLogoDialog} onClose={handleCloseLogoDialog}>
        <DialogTitle>Logo de {tenant?.company_name}</DialogTitle>
        <DialogContent>
          {tenant?.logo_url ? (
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <img
                src={tenant.logo_url}
                alt={`Logo ${tenant.company_name}`}
                style={{ maxWidth: '100%', maxHeight: '300px' }}
              />
            </Box>
          ) : (
            <Typography>Aucun logo disponible</Typography>
          )}
          {tenant?.signature_image && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Image de signature
              </Typography>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <img
                  src={tenant.signature_image}
                  alt="Signature"
                  style={{ maxWidth: '100%', maxHeight: '100px' }}
                />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {tenant.signature_name}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLogoDialog}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default FeatureManagement;
